import React, { type PropsWithChildren } from 'react';
import { StyleSheet, View, type ViewProps, type ViewStyle } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';

import { theme } from '@garnish/constants';

import { GlassView } from '../../../GlassView';

// ─────────────────────────────────────────────────────────────────────────────

export const ScreenHeaderContainer = (props: Props) => {
  const {
    children,
    isLiquidGlassEnabled = false,
    palette = 'brown',
    rounded = true,
    style,
  } = props;

  const { top: safeAreaTopInset } = useSafeAreaInsets();

  // ─── Styles ──────────────────────────────────────────────────────────

  const containerDynamicStyles = useStyle(() => {
    return { paddingTop: Math.max(safeAreaTopInset, theme.spacing['4']) };
  }, [safeAreaTopInset]);

  const containerStyles = [
    styles.container,
    rounded ? styles.roundedContainer : undefined,
    isLiquidGlassEnabled ? styles.containerGlass : paletteStyles[palette],
    containerDynamicStyles,
    style,
  ];
  const glassStyles = [styles.glass, rounded ? styles.roundedContainer : undefined];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={containerStyles}>
      {isLiquidGlassEnabled ? (
        <GlassView
          tintColor={GLASS_TINT_COLORS[palette]}
          glassEffectStyle="clear"
          style={glassStyles}
        />
      ) : null}

      {children}
    </View>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const GLASS_TINT_COLORS: Record<ScreenHeaderContainerPalette, string> = {
  brown: 'rgba(42, 23, 20, 0.99)',
  kale: 'rgba(0, 71, 60, 0.99)',
  spinach: 'rgba(45, 107, 82, 0.99)',
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    justifyContent: 'center',
    paddingHorizontal: theme.spacing['4'],
    paddingBottom: theme.spacing['4'],
  },
  roundedContainer: {
    borderBottomLeftRadius: theme.radius.large,
    borderBottomRightRadius: theme.radius.large,
  },
  containerGlass: {
    borderCurve: 'continuous',
  },
  glass: {
    ...StyleSheet.absoluteFill,
    borderCurve: 'continuous',
    pointerEvents: 'none',
  },
});

const paletteStyles = StyleSheet.create<Record<ScreenHeaderContainerPalette, ViewStyle>>({
  brown: { backgroundColor: theme.colors.BROWN },
  kale: { backgroundColor: theme.colors.KALE },
  spinach: { backgroundColor: theme.colors.SPINACH },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = PropsWithChildren<{
  isLiquidGlassEnabled?: boolean;
  palette?: ScreenHeaderContainerPalette;
  rounded?: boolean;
  style?: ViewProps['style'];
}>;

type ScreenHeaderContainerPalette = 'brown' | 'kale' | 'spinach';
